import {exam_getExaminationReportOnInfoSummary_Api, examinationSchema,} from 'apis'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useMemo} from 'react'
import {useLocation} from 'react-use'
import {branch, compose} from 'recompose'
import {useSelectEntities} from 'redux/entities/useSelectEntities'
import {ContentNotFound} from 'views/AppLocals/ContentNotFound'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import usePaginationHelper, {createPagerKey,} from 'views/AppLocals/hooks/usePaginationHelper'
import {NullAsDefault, renderElse} from 'views/Shared'
import {renderIf} from '../../Shared'
import {ExaminationGroupReportFilter} from './ExaminationGroupReportFilter'
import {withReportTable} from './renderReportTable'
import {useRouteSearchParam} from "../../../envs/_current/history";
import {formatDate} from "../../../helpers/datetimeHelpers";

const headers = [
    'name',
    'count paper',
    'count pass',
    'count 100',
    'max point',
    'min point',
    'avg point',
]

const schemas = [
    {
        name: 'title',
        value_path: 'title',
        className: 'font-medium text-color-000 whitespace-no-wrap',
    },
    {
        name: 'count_paper',
        value_path: 'count_paper',
        className: 'text-color-100',
    },
    {
        name: 'count_pass',
        value_path: 'count_pass',
        className: 'text-color-100',
    },
    {
        name: 'count_100',
        value_path: 'count_100',
        className: 'text-color-100',
    },
    {
        name: 'max_point',
        value_path: 'max_point',
        className: 'text-color-100',
    },
    {
        name: 'min_point',
        value_path: 'min_point',
        className: 'text-color-100',
    },
    {
        name: 'avg_point',
        value_path: 'avg_point',
        className: 'text-color-100',
    },
]

const ExaminationInfo = ({id}) => {
    const t = useTranslate()

    const item = useSelectEntities(id, examinationSchema, {})

    const {time_open, time_close, total_examination_paper} = item || {}

    const ItemInformation = ({label, value}) => (
        <div className="flex flex-col items-start">
      <span className="font-light text-xs text-color-400 italic">
        {t(label)}
          {': '}
      </span>
            <span className="font-semibold text-xs text-color-000 leading-tight">
        {renderIf(value, value)}
                {renderElse(value, '--')}
      </span>
        </div>
    )

    return renderElse(
        _.isEmpty(item),
        <div className="flex flex-col gap-4 mb-10 mt-4">
            <div className="text-center text-xl font-bold text-primary">
                {getTitle(item)}
            </div>
            <div className="grid grid-cols-3 gap-2 px-8">
                <ItemInformation
                    label="start"
                    value={formatDate(time_open, 'HH:mm - DD/MM/YYYY')}
                />
                <ItemInformation
                    label="end"
                    value={formatDate(time_close, 'HH:mm - DD/MM/YYYY')}
                />
                <ItemInformation
                    label="total exam paper"
                    value={total_examination_paper}
                />
            </div>
        </div>
    )
}

const ExaminationGroupReport = ({
                                    owner_id,
                                    owner_type,
                                    Wrapper = 'div',
                                    filter_actions = {
                                        sortBy: 'sort-by',
                                        keyword: 'keyword',
                                        examination_id: 'examination',
                                        examination_info_id: 'info',
                                    },
                                }) => {
    const location = useLocation()

    const t = useTranslate()

    const sortBy = useRouteSearchParam(filter_actions.sortBy)

    const examination_id = useRouteSearchParam(filter_actions.examination_id)

    const examination_info_id = useRouteSearchParam(filter_actions.examination_info_id)

    const refresh_deps = useMemo(
        () => [
            owner_id,
            owner_type,
            `refreshToken=${_.get(location.state, 'refreshToken')}`,
        ],
        [owner_id, owner_type, location.state]
    )

    const [pager_widget, {widget_header}] = usePaginationHelper({
        onInit: ({onSortChange = NullAsDefault, onFilterChange = NullAsDefault}) => {
            onSortChange(sortBy)
            onFilterChange({
                examination_id,
                examination_info_id,
            })
        },
        pagerInfo: {
            query: bindQueryParam({
                id: examination_id,
            }),
            PaginationMeta: NullAsDefault,
            apiInfo: exam_getExaminationReportOnInfoSummary_Api,
            ...withReportTable(headers, schemas),
        },
        withKey: createPagerKey(refresh_deps),
        dependencies: [owner_id, owner_type],
        filter_params: {
            owner_id,
            owner_type,
            examination_id,
            examination_info_id,
        },
        SortByWidget: NullAsDefault,
        renderSearchBar: NullAsDefault,
        isToggleDefault: true,
        query_params: filter_actions,
        AdvancedFilter: ExaminationGroupReportFilter,
    })
    return (
        <Wrapper className="space-y-3">
            <div className="font-semibold text-primary tracking-wide uppercase">
                {t('report')}
            </div>
            <div className="border rounded-lg p-3">
                <ExaminationInfo id={examination_id}/>
                <div className="flex flex-col">
                    <div className="text-center font-bold text-lg text-color-000 uppercase tracking-widest mb-4">
                        {t('report result')}
                    </div>
                    {examination_id ? pager_widget : widget_header}
                </div>
            </div>
        </Wrapper>
    )
}

export default compose(
    branch(
        ({owner_id, owner_type}) => _.some([owner_id, owner_type], _.isEmpty),
        () => () => <ContentNotFound/>
    )
)(ExaminationGroupReport)
